/**
 * 使用量の画面（UsagePage.tsx）の部品: 種類ごと・プロジェクトごと・日ごとのグラフ・定額の機能（2026-10-07 console/usage-view で UsagePage から分けた）。
 * WHY 分けるか: 種類ごと・プロジェクトごとを足して 1 ファイルが 450 行を超えた。画面の流れ（UsagePage）と、表とグラフの描き方（ここ）を分ける。
 *
 * 数字はサーバーが台帳と同じ数字で返す（apps/server modules/money/usageViews.ts = 種類・プロジェクトの合計は帳票の costMicros と同じ）。
 * ここで計算し直さない（足し直すと切り上げの所で照合表とずれる）。
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDay, formatPercent, translate, useT, type MessageKey } from '../../i18n';
import { money } from '../../money';
import type { DailyUsage } from '../../moneyApi';
import { daysOfPeriod, microsToCents } from '../../moneyLogic';
import { categoryAmount, type UsageRow } from '../../usageView';

const COMPONENT_KEYS: Record<string, MessageKey> = {
  players: 'comp.players',
  gamedata: 'comp.gamedata',
  payments: 'comp.payments',
  hosting: 'comp.hosting',
  realtime: 'comp.realtime',
  ai: 'comp.ai',
  ads: 'comp.ads',
  social: 'comp.social',
  liveops: 'comp.liveops',
  integrity: 'comp.integrity',
  compute: 'comp.compute',
  share: 'comp.share',
  platform: 'comp.platform'
};
/** 部品（古い本体の内訳）の表示名。知らない部品はそのまま */
export const componentLabel = (c: string) => (COMPONENT_KEYS[c] ? translate(COMPONENT_KEYS[c]) : c);

/**
 * 種類（サーバー usageViews.ts USAGE_CATEGORIES）の表示名。知らない種類（本体が先に種類を足した）は「その他」
 * = 中の名前（英語のキー）を画面に出さない
 */
const CATEGORY_KEYS: Record<string, MessageKey> = {
  database: 'use.cat.database',
  platform: 'use.cat.platform',
  functions: 'use.cat.functions',
  transfer: 'use.cat.transfer',
  storage: 'use.cat.storage',
  multiplayer: 'use.cat.multiplayer',
  domains: 'use.cat.domains',
  email: 'use.cat.email',
  ai: 'use.cat.ai',
  x_sign_in: 'use.cat.x_sign_in',
  other: 'use.cat.other'
};
export const categoryLabel = (c: string) => translate(CATEGORY_KEYS[c] ?? 'use.cat.other');
const rowLabel = (r: Pick<UsageRow, 'key' | 'source'>) => (r.source === 'category' ? categoryLabel(r.key) : componentLabel(r.key));

/**
 * 計測項目（meter）の表示名（2026-09-29 creator/launch-close-2・Money の決定）。functions_log_events = Functions のログの件数 →「ログ / Logs」。
 * WHY 名前を先に決めておくか: CLI・MCP の logs と同じ言葉にそろえ、項目名（英語の内部名）を画面に出さないため
 */
const METER_KEYS: Record<string, MessageKey> = {
  functions_log_events: 'use.meter.functions_log_events'
};
export const meterLabel = (m: string) => (METER_KEYS[m] ? translate(METER_KEYS[m]) : m);

/** マイクロ USD を表示する（1 セント未満も見えるように、小さい額は小数 4 桁まで） */
export function microsMoney(micros: number): string {
  const cents = microsToCents(micros);
  if (micros > 0 && cents < 1) return `$${(micros / 1_000_000).toFixed(4)}`;
  return money(Math.round(cents), 'usd');
}

const MAX_SERIES = 5;
const OTHER = '__other__';

/** 量の 1 行（「1,840,000 requests」「12.7 GB sent」）。言いようが無い種類は null */
function AmountText({ row, days }: { row: UsageRow; days: number }) {
  const { t } = useT();
  if (row.source !== 'category') return null;
  const a = categoryAmount(row.key, row.quantities, days);
  if (!a) return null;
  return <span className="muted small usage-qty">{t(`use.qty.${a.kind}` as MessageKey, { count: a.value, value: a.value })}</span>;
}

/**
 * 種類ごと（量と額）。共有プランの費用（月末の按分）・月末に戻った額も同じ表の行で見せる = 「今月いくら」の全部が 1 か所で分かる。
 * days = 量の平均（保存量・独自ドメイン）を出すための、その月の経った日数
 */
export function ByKindCard({ rows, days, sharedMicros, returnedCents }: { rows: UsageRow[]; days: number; sharedMicros: number; returnedCents: number }) {
  const { t } = useT();
  const total = rows.reduce((a, r) => a + r.costMicros, 0);
  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">{t('use.byKind')}</h2>
        <span className="muted small">{t('use.byKindNote')}</span>
      </div>
      <ul className="bar-list">
        {rows.map((r, i) => (
          <li key={`${r.source}:${r.key}`}>
            <div className="bar-list-row">
              <span className="usage-kind">
                <span>
                  <span className={`swatch swatch-${Math.min(i, MAX_SERIES)}`} aria-hidden="true" />
                  {rowLabel(r)}
                </span>
                <AmountText row={r} days={days} />
              </span>
              <span className="mono">
                {microsMoney(r.costMicros)} <span className="muted">· {formatPercent(total ? r.costMicros / total : 0, 0)}</span>
              </span>
            </div>
            <div className="meter" aria-hidden="true">
              <span className={`meter-fill-${Math.min(i, MAX_SERIES)}`} style={{ width: `${Math.max(1, total ? (r.costMicros / total) * 100 : 0)}%` }} />
            </div>
          </li>
        ))}
        {sharedMicros > 0 && (
          <li>
            <div className="bar-list-row">
              <span className="usage-kind">
                <span>{t('use.shared')}</span>
                <span className="muted small usage-qty">{t('use.sharedHint')}</span>
              </span>
              <span className="mono">{microsMoney(sharedMicros)}</span>
            </div>
          </li>
        )}
        {returnedCents > 0 && (
          <li>
            <div className="bar-list-row usage-returned">
              <span>{t('use.returnedRow')}</span>
              <span className="mono">−{money(returnedCents, 'usd')}</span>
            </div>
          </li>
        )}
      </ul>
    </section>
  );
}

/**
 * プロジェクトごと。行を押すとそのプロジェクトの概要へ（消したプロジェクトは押せない）。
 * WHY 本番の概要へ飛ぶか: 使用額はプロジェクト単位（両方のアプリの合計）で、概要の「今月の使用額」はどちらの画面でも同じ額。
 * 本番を既定にしたのは、使った額の多くは公開した方で出るから
 */
export function ByProjectCard({ projects, accountWide }: { projects: Array<{ projectId: string; name: string | null; costMicros: number }>; accountWide: boolean }) {
  const { t } = useT();
  const total = projects.reduce((a, p) => a + p.costMicros, 0);
  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">{t('use.byProject')}</h2>
        {accountWide && <span className="muted small">{t('use.byProjectNote')}</span>}
      </div>
      <ul className="bar-list">
        {projects.map((p) => (
          <li key={p.projectId}>
            <div className="bar-list-row">
              <span>{p.name !== null ? <Link to={`/p/${p.projectId}/production/overview`}>{p.name}</Link> : <span className="muted">{t('use.projectDeleted')}</span>}</span>
              <span className="mono">
                {microsMoney(p.costMicros)} <span className="muted">· {formatPercent(total ? p.costMicros / total : 0, 0)}</span>
              </span>
            </div>
            <div className="meter" aria-hidden="true">
              <span style={{ width: `${Math.max(1, total ? (p.costMicros / total) * 100 : 0)}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Kumo の定額の機能の欄の行（サーバー money/usage.ts・serviceFees.ts）。moneyApi.ts の型に無いので、ここで広げる（古い本体でも落ちないよう省略可） */
export type ServiceFeeLine = { feature: string; component: string; quantity: number; monthlyMicros: number; charged: number; refunded: number };
const ADDON_KEYS: Record<string, { label: MessageKey; detail: MessageKey }> = {
  slug_redirect: { label: 'use.addon.slug_redirect', detail: 'use.addon.slug_redirectDetail' }
};

/**
 * Kumo の定額の機能の欄（原価の種類ごととは別の行）。額は前払いから実際に引いた額（charged・月の累計で 1 回だけ切り捨て）。
 * WHY 使った機能だけを出すか: 定額の機能は任意（既定で使わない）。使っていない人に「$0」の行を並べると、料金が掛かる機能に見える
 */
export function AddonsCard({ fees }: { fees: ServiceFeeLine[] }) {
  const { t } = useT();
  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">{t('use.addons')}</h2>
        <span className="muted small">{t('use.addonsNote')}</span>
      </div>
      <ul className="bar-list">
        {fees.map((f) => {
          const keys = ADDON_KEYS[f.feature];
          return (
            <li key={f.feature}>
              <div className="bar-list-row">
                <span>
                  {keys ? t(keys.label) : f.feature}
                  {keys && <span className="muted small"> · {t(keys.detail, { count: f.quantity })}</span>}
                </span>
                <span className="mono">{money(f.charged, 'usd')}</span>
              </div>
              {f.refunded > 0 && <p className="muted small">{t('use.addonsRefunded', { amount: money(f.refunded, 'usd') })}</p>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * 日ごとの積み上げ棒（手書き SVG・依存なし = components/LineChart.tsx と同じ方針）。
 * order = 種類（または古い本体の部品）の並び = 種類ごとの表と同じ色の順。新しい本体は days[].byCategory、古い本体は byComponent を積む。
 * 多いと色が読めないので、上位 5 つ + その他 にまとめる
 */
export function DailyBars({ data, order, source }: { data: DailyUsage; order: string[]; source: UsageRow['source'] }) {
  const { t } = useT();
  // SVG の幅 = 実際の表示幅（1:1）。viewBox を固定にすると、スマホ幅では目盛りの文字が縮んで読めなくなる（2026-09-27 画面確認で発見）
  const ref = useRef<HTMLElement>(null);
  const [width, setWidth] = useState(720);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => e && setWidth(Math.max(280, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const label = (k: string) => (k === OTHER ? t('use.other') : rowLabel({ key: k, source }));
  const days = daysOfPeriod(data.period, new Date());
  // 6 つまではそのまま（6 色ある）。7 つ以上なら上位 5 つ + その他（1 つだけを「その他」にすると、表の名前と凡例が食い違う）
  const series = order.length <= MAX_SERIES + 1 ? order : order.slice(0, MAX_SERIES);
  const hasOther = series.length < order.length;
  const keys = hasOther ? [...series, OTHER] : series;
  const byDay = new Map(data.days.map((d) => [d.day, (source === 'category' ? d.byCategory : d.byComponent) ?? {}]));
  const stack = days.map((day) => {
    const d = byDay.get(day);
    const vals = keys.map((k) =>
      !d
        ? 0
        : k === OTHER
          ? Object.entries(d)
              .filter(([c]) => !series.includes(c))
              .reduce((a, [, v]) => a + v, 0)
          : (d[k] ?? 0)
    );
    return { day, vals, total: vals.reduce((a, b) => a + b, 0) };
  });
  const max = Math.max(...stack.map((s) => s.total), 1);
  const W = width;
  const H = 200;
  const padL = 56;
  const padB = 22;
  const innerW = W - padL - 8;
  const innerH = H - padB - 10;
  const bw = innerW / Math.max(days.length, 1);

  return (
    <figure className="bars" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t('use.chartLabel', { period: data.period })} className="bars-svg">
        <line x1={padL} x2={W - 8} y1={10} y2={10} className="chart-grid" />
        <line x1={padL} x2={W - 8} y1={10 + innerH / 2} y2={10 + innerH / 2} className="chart-grid" />
        <line x1={padL} x2={W - 8} y1={10 + innerH} y2={10 + innerH} className="chart-axis" />
        <text x={padL - 6} y={14} className="chart-tick" textAnchor="end">
          {microsMoney(max)}
        </text>
        <text x={padL - 6} y={10 + innerH} className="chart-tick" textAnchor="end">
          $0
        </text>
        {stack.map((s, i) => {
          let y = 10 + innerH;
          return (
            <g key={s.day}>
              <title>{`${formatDay(s.day)} · ${t('use.total')} ${microsMoney(s.total)}` + s.vals.map((v, k) => (v > 0 ? `\n${label(keys[k]!)} ${microsMoney(v)}` : '')).join('')}</title>
              {s.vals.map((v, k) => {
                const h = (v / max) * innerH;
                y -= h;
                return v > 0 ? <rect key={k} x={padL + i * bw + bw * 0.15} y={y} width={bw * 0.7} height={Math.max(h, 0.5)} className={`bar-fill-${k}`} /> : null;
              })}
              {(i === 0 || (i + 1) % 7 === 0) && (
                <text x={padL + i * bw + bw / 2} y={H - 6} className="chart-tick" textAnchor="middle">
                  {formatDay(s.day)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <figcaption className="legend">
        {keys.map((k, i) => (
          <span key={k} className="legend-item">
            <span className={`swatch swatch-${i}`} aria-hidden="true" />
            {label(k)}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}
